import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import { useAuth } from "@/hooks/useAuth";
import { useMyTrips } from "@/hooks/useMyTrips";
import { useReviewedBookings } from "@/hooks/useReviewedBookings";
import { useFlaggedBookings } from "@/hooks/useFlaggedBookings";
import { formatKES } from "@/lib/format";
import { tripPhase } from "@/lib/bookings";
import type { BookingWithVehicle } from "@/types";
import TripCard from "./components/TripCard";
import TripsEmpty from "./components/TripsEmpty";
import ReviewModal from "./components/ReviewModal";

type TabKey = "upcoming" | "past" | "all";

const TABS: { key: TabKey; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "all", label: "All trips" },
];

export default function TripsPage() {
  const { user } = useAuth();
  const { trips, loading, error, reload } = useMyTrips();
  const { reviewedIds, markReviewed } = useReviewedBookings();
  const { flagged } = useFlaggedBookings();
  const [tab, setTab] = useState<TabKey>("upcoming");
  const [reviewTarget, setReviewTarget] = useState<BookingWithVehicle | null>(null);

  const stats = useMemo(() => {
    const upcoming = trips.filter((trip) => tripPhase(trip) === "upcoming").length;
    const spent = trips
      .filter((trip) => trip.status && trip.status !== "cancelled" && trip.status !== "pending")
      .reduce((sum, trip) => sum + Number(trip.total_price ?? 0), 0);
    return { total: trips.length, upcoming, spent };
  }, [trips]);

  const visible = useMemo(() => {
    if (tab === "all") return trips;
    return trips.filter((trip) => tripPhase(trip) === tab);
  }, [trips, tab]);

  const flaggedTrips = useMemo(
    () => trips.filter((trip) => flagged.has(Number(trip.id))),
    [trips, flagged]
  );

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-28 md:px-8 md:pt-36">
        <div className="max-w-2xl">
          <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">Your bookings</span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">My trips</h1>
          <p className="mt-3 text-base leading-relaxed text-foreground-600">
            Every car you&apos;ve booked, with live payment status — all in one place.
          </p>
        </div>

        {!user ? (
          <div className="mt-10">
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
                <i className="ri-lock-2-line"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">Sign in to see your trips</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                Your bookings and their payment status live inside your Gari account.
              </p>
              <Link
                to="/auth?redirect=/trips"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-login-circle-line"></i> Sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: "Total trips", value: stats.total, icon: "ri-roadster-line" },
                { label: "Upcoming", value: stats.upcoming, icon: "ri-calendar-event-line" },
                { label: "Spent on Gari", value: formatKES(stats.spent), icon: "ri-wallet-3-line" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl surface-card p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/12 text-xl text-primary-400">
                    <i className={stat.icon}></i>
                  </span>
                  <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">{stat.value}</p>
                  <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 inline-flex rounded-full bg-background-100 p-1">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-5">
              {flaggedTrips.length > 0 && (
                <div className="flex flex-col gap-4 rounded-2xl border border-secondary-500/40 bg-secondary-500/10 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
                  <div className="flex items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-500 text-xl text-background-50">
                      <i className="ri-alert-line"></i>
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">
                        {flaggedTrips.length} {flaggedTrips.length === 1 ? "trip has" : "trips have"} damage flagged
                      </p>
                      <p className="mt-1 text-sm text-foreground-600">
                        Open the booking to review the before/after walkaround, add your side and resolve the dispute.
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/booking/${flaggedTrips[0].id}`}
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
                  >
                    <i className="ri-scales-3-line"></i> Review dispute
                  </Link>
                </div>
              )}

              {loading && (
                <>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div key={index} className="h-44 w-full animate-pulse rounded-2xl bg-background-100" />
                  ))}
                </>
              )}

              {!loading && error && (
                <div className="flex flex-col items-start gap-3 rounded-xl border border-secondary-700/40 bg-secondary-950/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="flex items-center gap-2 text-sm text-secondary-300">
                    <i className="ri-error-warning-line"></i> {error}
                  </p>
                  <button
                    type="button"
                    onClick={reload}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50"
                  >
                    <i className="ri-refresh-line"></i> Try again
                  </button>
                </div>
              )}

              {!loading && !error && visible.length === 0 && (
                <TripsEmpty
                  title={tab === "past" ? "No past trips yet" : "No trips booked yet"}
                  message={
                    tab === "past"
                      ? "Once you complete a trip it will show up here with your review options."
                      : "Browse the fleet and book your first car — it only takes a minute."
                  }
                  icon="ri-roadster-line"
                  actionLabel="Find a car"
                  to="/browse"
                />
              )}

              {!loading &&
                !error &&
                visible.map((trip, index) => (
                  <Reveal key={trip.id} delay={Math.min(index, 4) * 60}>
                    <TripCard trip={trip} reviewed={reviewedIds.has(trip.id)} onReview={setReviewTarget} flagged={flagged.has(Number(trip.id))} />
                  </Reveal>
                ))}
            </div>
          </>
        )}
      </div>

      {reviewTarget && (
        <ReviewModal
          booking={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onSubmitted={(bookingId) => {
            markReviewed(bookingId);
            setReviewTarget(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
}